/**
 * Annotation Editor Component
 * Visual editor for sentence_analysis annotations with token visualization
 * Features real-time effect preview during audio playback
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AnnotationEditor.css';
import {
  tokenize,
  parseAnnotation,
  buildAnnotation,
  ParsedAnnotation,
  getTokenRangeText,
  EFFECT_LABELS,
  EFFECT_COLORS,
} from '../../utils/tokenizer';

import { resolveMediaUrl } from '../../utils/mediaUrl';

interface SentenceAnalysis {
  annotations: string[];
  explanation_native: string;
  explanation_audio_url?: string;
  explanation_duration?: number;
}

interface AnnotationEditorProps {
  sentenceText: string;
  sentenceIndex: number;
  sentenceAnalysis: SentenceAnalysis | null;
  audioDuration?: number;
  onSave: (annotations: string[], explanation: string) => Promise<void>;
  onClose: () => void;
  onRegenerateAudio?: () => Promise<void>;
}

export const AnnotationEditor: React.FC<AnnotationEditorProps> = ({
  sentenceText,
  sentenceIndex,
  sentenceAnalysis,
  audioDuration,
  onSave,
  onClose,
  onRegenerateAudio,
}) => {
  const { t } = useTranslation(['creator', 'common']);

  // Tokenize sentence
  const tokens = useMemo(() => tokenize(sentenceText), [sentenceText]);

  // Parse existing annotations
  const initialAnnotations = useMemo(() => {
    if (!sentenceAnalysis?.annotations) return [];
    return sentenceAnalysis.annotations
      .map(parseAnnotation)
      .filter((a): a is ParsedAnnotation => a !== null);
  }, [sentenceAnalysis]);

  // State
  const [annotations, setAnnotations] = useState<ParsedAnnotation[]>(initialAnnotations);
  const [explanation, setExplanation] = useState(sentenceAnalysis?.explanation_native || '');
  const [selectingIndex, setSelectingIndex] = useState<number | null>(null);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Audio playback state
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioDur, setAudioDur] = useState(0);

  // Timeline drag state
  const timelineRef = useRef<HTMLDivElement>(null);
  const [draggingSegment, setDraggingSegment] = useState<{
    index: number;
    edge: 'left' | 'right' | 'move';
    startX: number;
    originalStart: number;
    originalEnd: number;
  } | null>(null);
  const [isSeeking, setIsSeeking] = useState(false);

  // Get active annotations based on current playback time
  const activeAnnotations = useMemo(() => {
    if (!isPlaying) return [];
    return annotations.filter(
      ann => currentTime >= ann.startTime && currentTime <= ann.endTime
    );
  }, [annotations, currentTime, isPlaying]);

  const audioUrl = useMemo(() => {
    const url = sentenceAnalysis?.explanation_audio_url;
    return url ? resolveMediaUrl(url) : null;
  }, [sentenceAnalysis?.explanation_audio_url]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleLoadedMetadata = () => {
      setAudioDur(audio.duration);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    // If already loaded
    if (audio.duration) {
      setAudioDur(audio.duration);
    }

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);

  // High-frequency time update for smooth cursor movement (50ms = 0.05s)
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      const audio = audioRef.current;
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const totalDuration = audioDuration || sentenceAnalysis?.explanation_duration || 30;

  // Play/Pause toggle
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  }, [isPlaying]);

  // Seek to time
  const seekTo = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(time, audioDur || totalDuration));
    setCurrentTime(audio.currentTime);
  }, [audioDur, totalDuration]);

  // Handle timeline click for seeking
  const handleTimelineClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (draggingSegment) return; // Don't seek while dragging segment
    const timeline = timelineRef.current;
    if (!timeline) return;

    const rect = timeline.getBoundingClientRect();
    const x = e.clientX - rect.left - 16; // 16px padding
    const width = rect.width - 32; // Account for padding
    const percent = Math.max(0, Math.min(1, x / width));
    const time = percent * (audioDur || totalDuration);
    seekTo(time);
  }, [draggingSegment, audioDur, seekTo, totalDuration]);

  // Handle segment edge drag start
  const handleSegmentDragStart = useCallback((
    e: React.MouseEvent,
    index: number,
    edge: 'left' | 'right' | 'move'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const ann = annotations[index];
    setDraggingSegment({
      index,
      edge,
      startX: e.clientX,
      originalStart: ann.startTime,
      originalEnd: ann.endTime,
    });
  }, [annotations]);

  // Handle mouse move for dragging
  useEffect(() => {
    if (!draggingSegment && !isSeeking) return;

    const handleMouseMove = (e: MouseEvent) => {
      const timeline = timelineRef.current;
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const width = rect.width - 32;
      const duration = audioDur || totalDuration;

      if (isSeeking) {
        const x = e.clientX - rect.left - 16;
        const percent = Math.max(0, Math.min(1, x / width));
        seekTo(percent * duration);
      }

      if (draggingSegment) {
        const deltaX = e.clientX - draggingSegment.startX;
        const deltaTime = (deltaX / width) * duration;

        setAnnotations(prev => {
          const updated = [...prev];
          const ann = { ...updated[draggingSegment.index] };

          if (draggingSegment.edge === 'left') {
            ann.startTime = Math.max(0, Math.min(
              draggingSegment.originalStart + deltaTime,
              ann.endTime - 0.1
            ));
          } else if (draggingSegment.edge === 'right') {
            ann.endTime = Math.max(
              ann.startTime + 0.1,
              Math.min(duration, draggingSegment.originalEnd + deltaTime)
            );
          } else if (draggingSegment.edge === 'move') {
            const segDuration = draggingSegment.originalEnd - draggingSegment.originalStart;
            let newStart = draggingSegment.originalStart + deltaTime;
            newStart = Math.max(0, Math.min(duration - segDuration, newStart));
            ann.startTime = newStart;
            ann.endTime = newStart + segDuration;
          }

          updated[draggingSegment.index] = ann;
          return updated;
        });
      }
    };

    const handleMouseUp = () => {
      setDraggingSegment(null);
      setIsSeeking(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingSegment, isSeeking, audioDur, seekTo, totalDuration]);

  // Check if a token is part of any annotation
  const getTokenAnnotations = useCallback((tokenIndex: number) => {
    return annotations.filter(
      a => tokenIndex >= a.startPos && tokenIndex <= a.endPos
    );
  }, [annotations]);

  // Get active effect for a token during playback
  const getActiveEffectForToken = useCallback((tokenIndex: number): ParsedAnnotation['effect'] | null => {
    if (!isPlaying) return null;
    const activeAnn = activeAnnotations.find(
      ann => tokenIndex >= ann.startPos && tokenIndex <= ann.endPos
    );
    return activeAnn?.effect || null;
  }, [isPlaying, activeAnnotations]);

  // Render sentence with effects
  const renderSentenceWithEffects = useCallback(() => {
    // Build segments based on token positions
    const segments: { text: string; effect: ParsedAnnotation['effect'] | null }[] = [];
    let currentSegment = { text: '', effect: null as ParsedAnnotation['effect'] | null };

    tokens.forEach((token, idx) => {
      const effect = getActiveEffectForToken(token.index);

      if (effect !== currentSegment.effect) {
        // Save current segment if it has content
        if (currentSegment.text) {
          segments.push({ ...currentSegment });
        }
        // Start new segment
        currentSegment = { text: token.text, effect };
      } else {
        // Continue current segment
        currentSegment.text += token.text;
      }

      // Push last segment
      if (idx === tokens.length - 1 && currentSegment.text) {
        segments.push({ ...currentSegment });
      }
    });

    return segments.map((segment, idx) => {
      if (!segment.effect) {
        return <span key={idx}>{segment.text}</span>;
      }

      const effectClass = `effect-${segment.effect}`;
      return (
        <span key={idx} className={effectClass}>
          {segment.effect === 'hide_text' ? '█'.repeat(segment.text.replace(/\s/g, '').length) : segment.text}
        </span>
      );
    });
  }, [tokens, getActiveEffectForToken]);

  // Handle token click for range selection
  const handleTokenClick = useCallback((tokenIndex: number) => {
    if (selectingIndex === null) return;

    if (selectionStart === null) {
      // First click - set start
      setSelectionStart(tokenIndex);
    } else {
      // Second click - set end and update annotation
      const start = Math.min(selectionStart, tokenIndex);
      const end = Math.max(selectionStart, tokenIndex);

      setAnnotations(prev => {
        const updated = [...prev];
        if (selectingIndex < updated.length) {
          updated[selectingIndex] = {
            ...updated[selectingIndex],
            startPos: start,
            endPos: end,
          };
        }
        return updated;
      });

      setSelectionStart(null);
      setSelectingIndex(null);
    }
  }, [selectingIndex, selectionStart]);

  // Add new annotation
  const handleAddAnnotation = useCallback(() => {
    const totalDuration = audioDuration || sentenceAnalysis?.explanation_duration || 10;
    const existingCount = annotations.length;
    const segmentDuration = totalDuration / (existingCount + 1);

    setAnnotations(prev => [
      ...prev,
      {
        effect: 'underline',
        startPos: 0,
        endPos: Math.min(2, tokens.length - 1),
        startTime: existingCount * segmentDuration,
        endTime: (existingCount + 1) * segmentDuration,
      },
    ]);
  }, [annotations.length, audioDuration, sentenceAnalysis, tokens.length]);

  // Remove annotation
  const handleRemoveAnnotation = useCallback((index: number) => {
    setAnnotations(prev => prev.filter((_, i) => i !== index));
    if (selectingIndex === index) {
      setSelectingIndex(null);
      setSelectionStart(null);
    }
  }, [selectingIndex]);

  // Update annotation field
  const updateAnnotation = useCallback((index: number, field: keyof ParsedAnnotation, value: any) => {
    setAnnotations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // Start range selection mode
  const startRangeSelection = useCallback((index: number) => {
    if (selectingIndex === index) {
      // Cancel selection
      setSelectingIndex(null);
      setSelectionStart(null);
    } else {
      setSelectingIndex(index);
      setSelectionStart(null);
    }
  }, [selectingIndex]);

  // Save changes
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const annotationStrings = annotations.map(buildAnnotation);
      console.log('💾 Saving annotations:', annotationStrings);
      console.log('💾 Saving explanation:', explanation);

      await onSave(annotationStrings, explanation);

      console.log('✅ Save successful!');
      setSuccess(true);

      // Auto-close modal after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('❌ Save failed:', err);
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [annotations, explanation, onSave, onClose]);

  // Regenerate audio
  const handleRegenerateAudio = useCallback(async () => {
    if (!onRegenerateAudio) return;
    setRegenerating(true);
    setError(null);

    try {
      await onRegenerateAudio();
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate audio');
    } finally {
      setRegenerating(false);
    }
  }, [onRegenerateAudio]);

  // Calculate timeline position
  const getTimelinePosition = useCallback((time: number) => {
    const total = audioDuration || sentenceAnalysis?.explanation_duration || 30;
    return (time / total) * 100;
  }, [audioDuration, sentenceAnalysis]);

  return (
    <div className="edit-modal-overlay" onClick={() => !saving && !success && onClose()}>
      <div className="edit-modal annotation-editor-modal" onClick={e => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h3>{t('creator:workflow.annotationEditor.title', { index: sentenceIndex })}</h3>
          <button className="btn-close" onClick={onClose} disabled={saving || success}>&times;</button>
        </div>

        <div className="edit-modal-content">
          {error && <div className="edit-error">❌ {error}</div>}
          {success && <div className="edit-success">{t('creator:workflow.annotationEditor.saveSuccess')}</div>}

          {/* Hidden audio element */}
          {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" style={{ display: 'none' }} />}

          {/* Sentence Display with Live Effects */}
          <div className="sentence-display">
            <div className="sentence-display-label">
              {t('creator:workflow.annotationEditor.originalSentence')}
              <button
                className={`btn-inline-play ${isPlaying ? 'playing' : ''} ${!audioUrl ? 'disabled' : ''}`}
                onClick={togglePlayPause}
                disabled={!audioUrl}
                title={audioUrl ? (isPlaying ? t('creator:workflow.annotationEditor.pause') : t('creator:workflow.annotationEditor.play')) : t('creator:workflow.annotationEditor.noAudio')}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              {isPlaying && <span className="playing-indicator">{t('creator:workflow.annotationEditor.playing')}</span>}
            </div>
            <div className={`sentence-text ${isPlaying ? 'with-effects' : ''}`}>
              {isPlaying ? renderSentenceWithEffects() : sentenceText}
            </div>
          </div>

          {/* Token Grid */}
          <div className="token-grid">
            <div className="token-grid-label">
              {t('creator:workflow.annotationEditor.tokenPositions')}
              {selectingIndex !== null && (
                <span className="token-grid-hint">
                  {t('creator:workflow.annotationEditor.clickToSelect', { index: selectingIndex + 1 })}
                  {selectionStart !== null && ` (${t('creator:workflow.annotationEditor.selectionStart', { start: selectionStart })})`}
                </span>
              )}
            </div>
            <div className="token-container">
              {tokens.map(token => {
                const tokenAnnotations = getTokenAnnotations(token.index);
                const isSelecting = selectingIndex !== null;
                const isSelected = selectionStart === token.index;
                // Check if this token is within the current annotation being edited
                const isCurrentAnnotationRange = selectingIndex !== null &&
                  selectingIndex < annotations.length &&
                  token.index >= annotations[selectingIndex].startPos &&
                  token.index <= annotations[selectingIndex].endPos;

                const classNames = [
                  'token-item',
                  token.type === 'space' ? 'space' : '',
                  token.type === 'punctuation' ? 'punctuation' : '',
                  isSelecting ? 'selectable' : '',
                  isSelected ? 'selected' : '',
                  isCurrentAnnotationRange ? 'in-range' : '',
                  ...tokenAnnotations.map(a => `has-annotation-${a.effect}`),
                ].filter(Boolean).join(' ');

                return (
                  <div
                    key={token.index}
                    className={classNames}
                    onClick={() => isSelecting && handleTokenClick(token.index)}
                    title={`Token ${token.index}: "${token.text}"`}
                  >
                    <span className="token-index">{token.index}</span>
                    <span className={`token-text ${token.type}`}>
                      {token.type === 'space' ? '␣' : token.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timeline Visualization */}
          <div className="timeline-section">
            <div className="timeline-label">
              {t('creator:workflow.annotationEditor.timeline', { duration: (audioDur || totalDuration).toFixed(1) })}
              <button
                className={`btn-inline-play ${isPlaying ? 'playing' : ''} ${!audioUrl ? 'disabled' : ''}`}
                onClick={togglePlayPause}
                disabled={!audioUrl}
                title={audioUrl ? (isPlaying ? t('creator:workflow.annotationEditor.pause') : t('creator:workflow.annotationEditor.play')) : t('creator:workflow.annotationEditor.noAudio')}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              <span className="timeline-hint">{t('creator:workflow.annotationEditor.dragToAdjust')}</span>
            </div>
            <div
              className="timeline-container"
              ref={timelineRef}
              onClick={handleTimelineClick}
            >
              <div className="timeline-track" />

              {/* Playback cursor - always visible */}
              <div
                className={`timeline-cursor ${isPlaying ? 'playing' : ''}`}
                style={{ left: `calc(16px + ${getTimelinePosition(currentTime)}%)` }}
              >
                <span className="timeline-cursor-time">{currentTime.toFixed(2)}s</span>
              </div>

              {/* Annotation segments with drag handles */}
              {annotations.map((ann, idx) => {
                const left = getTimelinePosition(ann.startTime);
                const width = getTimelinePosition(ann.endTime) - left;
                const isActive = currentTime >= ann.startTime && currentTime <= ann.endTime;
                const isDragging = draggingSegment?.index === idx;

                return (
                  <div
                    key={idx}
                    className={`timeline-segment ${ann.effect} ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
                    style={{
                      left: `calc(16px + ${left}%)`,
                      width: `calc(${width}% - 32px * ${width / 100})`,
                      backgroundColor: EFFECT_COLORS[ann.effect],
                    }}
                    title={t('creator:workflow.annotationEditor.dragSegmentTitle', { effect: EFFECT_LABELS[ann.effect], start: ann.startTime.toFixed(2), end: ann.endTime.toFixed(2) })}
                    onMouseDown={(e) => handleSegmentDragStart(e, idx, 'move')}
                  >
                    {/* Left drag handle */}
                    <div
                      className="segment-handle segment-handle-left"
                      onMouseDown={(e) => handleSegmentDragStart(e, idx, 'left')}
                      title={t('creator:workflow.annotationEditor.dragStartHandle')}
                    />
                    <span className="segment-label">{idx + 1}</span>
                    {/* Right drag handle */}
                    <div
                      className="segment-handle segment-handle-right"
                      onMouseDown={(e) => handleSegmentDragStart(e, idx, 'right')}
                      title={t('creator:workflow.annotationEditor.dragEndHandle')}
                    />
                  </div>
                );
              })}

              <div className="timeline-time-markers">
                <span>0s</span>
                <span>{((audioDur || totalDuration) / 4).toFixed(0)}s</span>
                <span>{((audioDur || totalDuration) / 2).toFixed(0)}s</span>
                <span>{((audioDur || totalDuration) * 3 / 4).toFixed(0)}s</span>
                <span>{(audioDur || totalDuration).toFixed(0)}s</span>
              </div>
            </div>
          </div>

          {/* Annotations List */}
          <div className="annotations-section">
            <div className="annotations-section-header">
              <div className="annotations-section-label">{t('creator:workflow.annotationEditor.annotations', { count: annotations.length })}</div>
              <button className="btn-add-annotation" onClick={handleAddAnnotation}>
                {t('creator:workflow.annotationEditor.addAnnotation')}
              </button>
            </div>

            {annotations.length === 0 ? (
              <div className="annotations-empty">
                <div className="annotations-empty-icon">📝</div>
                <div>{t('creator:workflow.annotationEditor.noAnnotations')}</div>
              </div>
            ) : (
              <div className="annotations-list">
                {annotations.map((ann, idx) => {
                  const isActive = isPlaying && currentTime >= ann.startTime && currentTime <= ann.endTime;
                  return (
                    <div
                      key={idx}
                      className={`annotation-item ${selectingIndex === idx ? 'editing' : ''} ${isActive ? 'playback-active' : ''}`}
                    >
                      <div className="annotation-number">{idx + 1}</div>

                      <div className="annotation-content">
                        <div className="annotation-row">
                          {/* Effect Type */}
                          <select
                            className="annotation-effect-select"
                            value={ann.effect}
                            onChange={e => updateAnnotation(idx, 'effect', e.target.value)}
                            style={{ borderColor: EFFECT_COLORS[ann.effect] }}
                          >
                            {Object.entries(EFFECT_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>

                          {/* Position */}
                          <div className="annotation-position">
                            <span className="annotation-position-label">{t('creator:workflow.annotationEditor.position')}</span>
                            <input
                              type="number"
                              className="annotation-position-input"
                              value={ann.startPos}
                              onChange={e => updateAnnotation(idx, 'startPos', parseInt(e.target.value) || 0)}
                              min={0}
                              max={tokens.length - 1}
                            />
                            <span className="annotation-position-arrow">→</span>
                            <input
                              type="number"
                              className="annotation-position-input"
                              value={ann.endPos}
                              onChange={e => updateAnnotation(idx, 'endPos', parseInt(e.target.value) || 0)}
                              min={0}
                              max={tokens.length - 1}
                            />
                          </div>

                          {/* Time */}
                          <div className="annotation-time">
                            <span className="annotation-time-label">{t('creator:workflow.annotationEditor.time')}</span>
                            <input
                              type="number"
                              className="annotation-time-input"
                              value={ann.startTime.toFixed(2)}
                              onChange={e => updateAnnotation(idx, 'startTime', parseFloat(e.target.value) || 0)}
                              step="0.1"
                              min={0}
                            />
                            <span className="annotation-time-unit">s</span>
                            <span className="annotation-position-arrow">→</span>
                            <input
                              type="number"
                              className="annotation-time-input"
                              value={ann.endTime.toFixed(2)}
                              onChange={e => updateAnnotation(idx, 'endTime', parseFloat(e.target.value) || 0)}
                              step="0.1"
                              min={0}
                            />
                            <span className="annotation-time-unit">s</span>
                          </div>
                        </div>

                        {/* Preview */}
                        <div className="annotation-preview">
                          <span className="annotation-preview-label">{t('creator:workflow.annotationEditor.text')}</span>
                          <span className="annotation-preview-text">
                            "{getTokenRangeText(tokens, ann.startPos, ann.endPos)}"
                          </span>
                        </div>
                      </div>

                      <div className="annotation-actions">
                        <button
                          className={`btn-select-range ${selectingIndex === idx ? 'active' : ''}`}
                          onClick={() => startRangeSelection(idx)}
                          title={t('creator:workflow.annotationEditor.selectRange')}
                        >
                          {selectingIndex === idx ? t('creator:workflow.annotationEditor.cancelSelect') : t('creator:workflow.annotationEditor.select')}
                        </button>
                        <button
                          className="btn-remove-annotation"
                          onClick={() => handleRemoveAnnotation(idx)}
                          title={t('creator:workflow.annotationEditor.removeAnnotation')}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Explanation Text */}
          <div className="explanation-section">
            <div className="explanation-label">{t('creator:workflow.annotationEditor.explanationText')}</div>
            <textarea
              className="explanation-textarea"
              value={explanation}
              onChange={e => setExplanation(e.target.value)}
              placeholder={t('creator:workflow.annotationEditor.explanationPlaceholder')}
            />
          </div>
        </div>

        <div className="edit-modal-footer">
          {onRegenerateAudio && (
            <button
              className="btn-regenerate"
              onClick={handleRegenerateAudio}
              disabled={regenerating || saving}
            >
              {regenerating ? t('creator:workflow.annotationEditor.regenerating') : t('creator:workflow.annotationEditor.regenerateAudio')}
            </button>
          )}
          <button className="btn-cancel" onClick={onClose} disabled={saving || success}>
            {t('common:cancel')}
          </button>
          <button className="btn-save" onClick={handleSave} disabled={saving || success}>
            {saving ? t('creator:workflow.annotationEditor.saving') : success ? t('creator:workflow.annotationEditor.saved') : t('creator:workflow.annotationEditor.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnotationEditor;
